import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import type { CollaborationHelpContext as CollaborationHelpResolved } from "@shared/collaborationHelpContext";
import {
  isCollaborationWorkspacePath,
  resolveCollaborationHelpContext,
} from "@shared/collaborationHelpContext";
import { useUserContext } from "@/context/UserContext";

export type CollaborationHelpMatchSnapshot = {
  status?: string | null;
  requesterCompanyId?: string | null;
  receiverCompanyId?: string | null;
  requesterPaid?: boolean | null;
  receiverPaid?: boolean | null;
  collaborationReadOnly?: boolean;
};

type CollaborationHelpProviderValue = {
  isWorkspaceRoute: boolean;
  resolved: CollaborationHelpResolved | null;
  setMatchSnapshot: (snap: CollaborationHelpMatchSnapshot | null) => void;
};

const CollaborationHelpReactContext = createContext<CollaborationHelpProviderValue | null>(null);

/**
 * Publishes workspace help state so the header robot Ask tab can show situation chips
 * without re-fetching the match.
 */
export function CollaborationHelpProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { userDetails } = useUserContext();
  const [matchSnapshot, setMatchSnapshotState] = useState<CollaborationHelpMatchSnapshot | null>(
    null,
  );

  const setMatchSnapshot = useCallback((snap: CollaborationHelpMatchSnapshot | null) => {
    setMatchSnapshotState(snap);
  }, []);

  const isWorkspaceRoute = isCollaborationWorkspacePath(location.pathname);

  const resolved = useMemo(() => {
    if (!isWorkspaceRoute) return null;

    const match =
      matchSnapshot &&
      (matchSnapshot.status || matchSnapshot.requesterCompanyId || matchSnapshot.receiverCompanyId)
        ? {
            status: matchSnapshot.status,
            requesterCompanyId: matchSnapshot.requesterCompanyId,
            receiverCompanyId: matchSnapshot.receiverCompanyId,
            requesterPaid: matchSnapshot.requesterPaid,
            receiverPaid: matchSnapshot.receiverPaid,
          }
        : null;

    return resolveCollaborationHelpContext({
      pathname: location.pathname,
      match,
      userCompanyId: userDetails?.company?.id ?? null,
      collaborationReadOnly: Boolean(matchSnapshot?.collaborationReadOnly),
    });
  }, [isWorkspaceRoute, location.pathname, matchSnapshot, userDetails?.company?.id]);

  const value = useMemo(
    () => ({
      isWorkspaceRoute,
      resolved,
      setMatchSnapshot,
    }),
    [isWorkspaceRoute, resolved, setMatchSnapshot],
  );

  return (
    <CollaborationHelpReactContext.Provider value={value}>
      {children}
    </CollaborationHelpReactContext.Provider>
  );
}

export function useCollaborationHelp(): CollaborationHelpProviderValue {
  const ctx = useContext(CollaborationHelpReactContext);
  if (!ctx) {
    return {
      isWorkspaceRoute: false,
      resolved: null,
      setMatchSnapshot: () => undefined,
    };
  }
  return ctx;
}
